import React from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecksIcon, PlusIcon } from '@phosphor-icons/react';
import { Button, Dialog } from '@origo/origo-ui';
import { EmptyState } from '@geodata/georg';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import YearGroupHeader from './components/YearGroupHeader';
import ProcessingBlock from './components/ProcessingBlock';
import NewProcessingDialog from './components/NewProcessingDialog';
import type { ProcessingFieldErrors, ProcessingFormOptions } from './components/ProcessingForm';
import type { ProcessingFormValues, ProcessingItem, ProcessingYearGroup } from '../../types';
import styles from './ProcessingsPage.module.css';

export interface NewProcessingState {
    open: boolean;
    values: ProcessingFormValues;
    fieldErrors: ProcessingFieldErrors;
    isSaving: boolean;
}

interface ProcessingsPageProps {
    groups: ProcessingYearGroup[];
    openGroups: ReadonlySet<string>;
    options: ProcessingFormOptions;
    onGroupToggle: (key: string) => void;

    // Opprett (#14408)
    newProcessing: NewProcessingState;
    onNewOpen: () => void;
    onNewClose: () => void;
    onNewChange: (field: keyof ProcessingFormValues, value: string) => void;
    onNewSave: () => void;

    // Rediger/slett (#14409)
    fieldErrorsById: Record<string, ProcessingFieldErrors | undefined>;
    onItemSave: (id: string, values: ProcessingFormValues) => void;
    onItemDeleteRequest: (id: string) => void;
    onShowDocuments: (planbehUid: string) => void;
    pendingDelete: ProcessingItem | null;
    onDeleteConfirm: () => void;
    onDeleteCancel: () => void;
    isSaving?: boolean;
    isDeleting?: boolean;
}

const ProcessingsPage: React.FC<ProcessingsPageProps> = ({
    groups,
    openGroups,
    options,
    onGroupToggle,
    newProcessing,
    onNewOpen,
    onNewClose,
    onNewChange,
    onNewSave,
    fieldErrorsById,
    onItemSave,
    onItemDeleteRequest,
    onShowDocuments,
    pendingDelete,
    onDeleteConfirm,
    onDeleteCancel,
    isSaving,
    isDeleting,
}) => {
    const { t } = useTranslation();

    const isEmpty = groups.length === 0 || groups.every(g => g.items.length === 0);

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('processings.title')}
                subtitle={t('processings.subtitle')}
                actions={
                    !isEmpty && (
                        <Button variant="primary" onClick={onNewOpen} leftIcon={<PlusIcon size={16} weight="bold" />}>
                            {t('processings.empty.action')}
                        </Button>
                    )
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<ListChecksIcon size={32} weight="duotone" />}
                        title={t('processings.empty.title')}
                        description={t('processings.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={onNewOpen}>
                                {t('processings.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    groups.map(group => (
                        <YearGroupHeader
                            key={group.key}
                            year={group.year}
                            count={group.items.length}
                            open={openGroups.has(group.key)}
                            onToggle={() => onGroupToggle(group.key)}
                        >
                            <ul className={styles.itemList}>
                                {group.items.map(item => (
                                    <li key={item.id}>
                                        <ProcessingBlock
                                            item={item}
                                            options={options}
                                            fieldErrors={fieldErrorsById[item.id]}
                                            onSave={values => onItemSave(item.id, values)}
                                            onDelete={() => onItemDeleteRequest(item.id)}
                                            onShowDocuments={onShowDocuments}
                                            isSaving={isSaving}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </YearGroupHeader>
                    ))
                )}
            </div>

            <NewProcessingDialog
                open={newProcessing.open}
                values={newProcessing.values}
                fieldErrors={newProcessing.fieldErrors}
                options={options}
                onChange={onNewChange}
                onClose={onNewClose}
                onSave={onNewSave}
                isSaving={newProcessing.isSaving}
            />

            <Dialog
                open={pendingDelete !== null}
                onClose={onDeleteCancel}
                title={t('processings.delete.title')}
                footer={
                    <div className={styles.dialogFooter}>
                        <Button variant="secondary" onClick={onDeleteCancel} disabled={isDeleting}>
                            {t('common.cancel')}
                        </Button>
                        <Button variant="danger" onClick={onDeleteConfirm} disabled={isDeleting}>
                            {t('processings.delete.confirm')}
                        </Button>
                    </div>
                }
            >
                <p>{t('processings.delete.body', { navn: pendingDelete?.tittel ?? '' })}</p>
            </Dialog>
        </article>
    );
};

export default ProcessingsPage;
